import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { AddonService, AreaOfUseService, CatalogKeysService, CategoryService } from '../../../../core/catalog';
import { AREA_OF_USE_ALL_CATEGORIES_CODE, AddonGroup, AddonNode, Category, SolrSearchResult } from '../../../../core/model';
import { prepareUrlForLink } from '../../../../core/util';

@Component({
  selector: 'py-catalog-breadcrumb',
  templateUrl: './catalog-breadcrumb.component.html',
  styleUrls: ['./catalog-breadcrumb.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CatalogBreadcrumbComponent implements OnInit, OnDestroy {
  areaOfUseCategories: Category[] = [];
  categories: Category[] = [];
  addons: Array<AddonGroup | AddonNode> = [];
  private _addonGroup: AddonGroup;
  private _addonNode: AddonNode;

  @Input() showAllCategoriesLink?: boolean;
  @Input() searchResults?: SolrSearchResult;

  @Input()
  set category(category: Category | undefined) {
    this.categories = [];
    this.areaOfUseCategories = [];

    if (category) {
      this.hasAreaOfUse ? this.populateParentCategoriesForAreaOfUse(category) : this.populateParentCategories(category);
    }
  }

  @Input()
  set addonGroup(addonGroup: AddonGroup | undefined) {
    this.addons = [];
    this._addonGroup = addonGroup;
    if (addonGroup) {
      this.populateParentAddonGroups(addonGroup);
    }
  }
  get addonGroup(): AddonGroup {
    return this._addonGroup;
  }

  @Input() set addonNode(addonNode: AddonNode | undefined) {
    this._addonNode = addonNode;
  }
  get addonNode(): AddonNode {
    return this._addonNode;
  }

  get areaOfUseAllCategoriesCode() {
    return AREA_OF_USE_ALL_CATEGORIES_CODE;
  }

  myaQueryParamValue: string;
  sortQueryParamValue$: Observable<string>;
  hasAreaOfUse: boolean;

  subscription = new Subscription();
  @Input() showBorder? = true;

  constructor(
    private categoryService: CategoryService,
    private addonService: AddonService,
    private activatedRoute: ActivatedRoute,
    private catalogKeysService: CatalogKeysService,
    private router: Router,
    private areaOfUseService: AreaOfUseService
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.activatedRoute.queryParams.pipe(map((params) => !!(params && params['aou']))).subscribe((hasAreaOfUse) => {
        this.hasAreaOfUse = hasAreaOfUse;
      })
    );

    this.myaQueryParamValue = this.activatedRoute.snapshot?.queryParams?.mya || '0';
    this.sortQueryParamValue$ = this.activatedRoute.queryParams.pipe(map((params) => params?.sort));
  }

  private populateParentCategoriesForAreaOfUse(category: Category): void {
    if (!this.areaOfUseCategories.find((c) => c.code === category.code)) {
      this.areaOfUseCategories.unshift(category);
      if (category.superCategoryRef) {
        this.areaOfUseService.getAreaOfUse(category.superCategoryRef).subscribe((superCategory) => {
          if (!this.areaOfUseCategories.find((c) => c.code === superCategory.code)) {
            this.populateParentCategoriesForAreaOfUse(superCategory);
          }
        });
      }
    }
  }

  private populateParentCategories(category: Category): void {
    if (!this.categories.find((c) => c.code === category.code)) {
      this.categories.unshift(category);
      if (category.superCategoryRef) {
        this.subscription.add(
          this.catalogKeysService
            .getCatalogUsageKey()
            .pipe(
              take(1),
              switchMap((catalogUsageKey) => this.categoryService.getCategory(category.superCategoryRef, catalogUsageKey))
            )
            .subscribe((theCategory) => {
              if (!this.categories.find((c) => c.code === theCategory.code)) {
                this.populateParentCategories(theCategory);
              }
            })
        );
      }
    }
  }

  private populateParentAddonGroups(addonGroup: AddonGroup): void {
    if (!this.addons.find((c) => c.id === addonGroup.id)) {
      this.addons.unshift(addonGroup);
      if (addonGroup.superGroupId) {
        this.subscription.add(
          this.addonService
            .getAddonGroup(addonGroup.superGroupId)
            .pipe(take(1))
            .subscribe((theAddon) => {
              if (!this.addons.find((a) => a.id === theAddon.id)) {
                this.populateParentAddonGroups(theAddon);
              }
            })
        );
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  getCategoryUrl(category: Category): string {
    const currentQueryParams = this.activatedRoute.snapshot.queryParams;
    let query: string = currentQueryParams.query;
    const productCode = `:productCode:${this.activatedRoute.snapshot.data?.productCode}`;
    const articleCode = `:articleCode:${currentQueryParams.a}`;

    if (!!query?.match(productCode)) {
      query = query.replace(productCode, '');
    }

    if (!!query?.match(articleCode)) {
      query = query.replace(articleCode, '');
    }

    if (!!query?.match(/allCategories:[^:&]+/)) {
      query = query.replace(/allCategories:[^:&]+/, `allCategories:${category.code}`);
    } else {
      query = query || '::';
      query = `${query}${query.endsWith(':') ? '' : ':'}allCategories:${category.code}`;
    }

    return prepareUrlForLink(category.url, {
      ...currentQueryParams,
      query: query,
      p: null,
      a: null,
      pageSize: null,
    });
  }

  getAreaOfUseCategoryUrl(category: Category): string {
    const currentQueryParams = this.activatedRoute.snapshot.queryParams;
    let query: string = currentQueryParams.query;

    if (!!query?.match(/articleAreasOfUse:[^:&]+/)) {
      query = query.replace(/articleAreasOfUse:[^:&]+/, `articleAreasOfUse:${category.code}`);
    } else {
      query = query || '::';
      query = `${query}${query.endsWith(':') ? '' : ':'}articleAreasOfUse:${category.code}`;
    }

    const categoryUrl = category.url.replace('{category-name}', category.name);
    return prepareUrlForLink(categoryUrl, {
      ...currentQueryParams,
      aou: category.code,
      query: query,
      p: null,
      a: null,
    });
  }

  clearSearch(): void {
    let query = null;
    const currentQuery = this.searchResults?.currentQuery?.query?.value;

    if (currentQuery) {
      query = currentQuery.substr(currentQuery.indexOf(':'));
    }

    this.router.navigate([], {
      queryParams: { query },
      queryParamsHandling: 'merge',
      relativeTo: this.activatedRoute,
    });
  }
}
